import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import * as authActions from 'global/actions/authActions';
import * as signUpActions from 'global/actions/signUpActions';
import { useActions } from 'hooks/useActions';
import { FreecodeInputContainer, FreecodeInputField, FreecodeIcon } from 'components/FormComponents/FormComponents';
import { FreecodeCheckbox } from 'components/Checkbox/Checkbox';
import { agreementLabel, GDPRLabel } from 'pages/SignupPage/strings';
import SignInWithGoogle from 'components/ExternalSignIn/SignInWithGoogle';
import SignInWithLinkedIn from 'components/ExternalSignIn/SignInWithLinkedIn';
import Divider from 'components/Divider/Divider';
import { BenefitsList, BenefitsListItem } from './styles';
import { Header, Body, Footer, FooterButton } from './freecodeModalElements';

const english = 'en';

// Get fragment with modal sign up functionality
export default function ModalContentSignUp({ email, setEmail, onCancel, goToLogin }) {
  const { initialize } = useActions(authActions);

  const { loading: signupLoading, error: signupError, success: signupSuccess } = useSelector((state) => state.signUp);
  const { clearError: signupClearError, clearState: signupClearState, signup } = useActions(signUpActions);

  const [isAgreementAgreed, setIsAgreementAgreed] = useState(false);
  const [isGDPRAgreed, setIsGDPRAgreed] = useState(false);
  const [signUpDone, setSignUpDone] = useState(false);

  useEffect(() => {
    signupClearState();
  }, [signupClearState]);

  useEffect(() => {
    if (signupError) {
      toast.error(
        signupError?.[english] ? signupError[english] : typeof signupError === 'object' ? signupError?.[0] : signupError
      );
      signupClearError();
    }
  }, [signupError, signupClearError]);

  useEffect(() => {
    if (signupSuccess) {
      setSignUpDone(true);
      let interval = setInterval(() => initialize(), 1000);
      return () => {
        clearInterval(interval);
        signupClearState();
      };
    }
  }, [signupSuccess, setSignUpDone, initialize, signupClearState]);

  const signUpModalBody = () => (
    <>
      <Header>Sign up to get these benefits</Header>
      <Body loading={signupLoading}>
        <BenefitsList>
          <BenefitsListItem>Save your progress as you solve exercises</BenefitsListItem>
          <BenefitsListItem>Earn achievements and experience points</BenefitsListItem>
          <BenefitsListItem>Compete against the community in exciting challenges</BenefitsListItem>
        </BenefitsList>
        <FreecodeInputContainer>
          <FreecodeIcon className="fa-solid fa-envelope" />
          <FreecodeInputField
            type="text"
            autoFocus
            value={email}
            placeholder="Email"
            onChange={(e) => setEmail(e.target.value)}
          />
        </FreecodeInputContainer>
        <FreecodeCheckbox
          isSignUpModal
          usePadding="0 0 5px"
          checked={isAgreementAgreed}
          onChange={() => setIsAgreementAgreed(!isAgreementAgreed)}
          label={agreementLabel[english]}
        />
        <FreecodeCheckbox
          isSignUpModal
          usePadding="0 0 5px"
          checked={isGDPRAgreed}
          onChange={() => setIsGDPRAgreed(!isGDPRAgreed)}
          label={GDPRLabel[english]}
        />
        <i>Note: Your current code will remain</i>

        <FooterButton
          margin="10px 0 0"
          isFreecodeAuthPrimaryButton
          onClick={() => signup(email, '', '', false, isAgreementAgreed, isGDPRAgreed)}
          disabled={signupLoading || !email || !isAgreementAgreed || !isGDPRAgreed}
        >
          Sign up
        </FooterButton>
        <Divider text="or" />
        <SignInWithGoogle />
        <SignInWithLinkedIn />
        <FooterButton margin="20px 0 0" isFreecodeAuthSecondaryButton onClick={goToLogin}>
          Log In
        </FooterButton>
        <FooterButton isFreecodeAuthCancelButton onClick={onCancel}>
          Cancel
        </FooterButton>
      </Body>
    </>
  );

  const signUpSuccessBody = () => (
    <>
      <Header>Success</Header>
      <Body>
        <p>
          An email has been sent with instructions on how to complete the signup process. Didn't get the email? Remember
          to check the spam folder
        </p>
        <p>Leave this modal open to automatically sign in when the email instructions have been completed.</p>
      </Body>
      <Footer>
        <FooterButton onClick={onCancel} label="Close">
          Close
        </FooterButton>
      </Footer>
    </>
  );

  return !signUpDone ? signUpModalBody() : signUpSuccessBody();
}
